import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { MessageSquare, Send, History, ArrowLeft, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { FeedbackSubmitForm, FeedbackList } from '../components/FeedbackSystem';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SEO } from '../components/SEO';

function FeedbackPage() {
  const { isAuthenticated, isContributor, user } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Show upgrade message if not a contributor
  if (!isContributor) {
    return (
      <div className="min-h-screen py-12 page-padding" data-testid="feedback-upgrade">
        <SEO title="Creator Feedback" description="Submit feedback on comics and earn money when creators use your ideas." />
        
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-[var(--primary)]/10 flex items-center justify-center mx-auto mb-6">
            <Lock size={40} className="text-[var(--primary)]" />
          </div>
          
          <h1 className="text-4xl font-teko mb-4">Contributor Access Required</h1>
          <p className="text-[var(--text-secondary)] mb-8 max-w-md mx-auto">
            The feedback system is exclusive to Contributors. Upgrade your subscription to submit feedback and earn money when creators use your ideas!
          </p>
          
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 mb-8">
            <h3 className="font-teko text-xl mb-4">Contributor Benefits</h3>
            <ul className="text-left space-y-3">
              <li className="flex items-start gap-3">
                <MessageSquare size={18} className="text-[var(--primary)] mt-0.5" />
                <span>Submit feedback on any comic</span>
              </li>
              <li className="flex items-start gap-3">
                <Send size={18} className="text-green-500 mt-0.5" />
                <span>Earn money when your ideas are used</span>
              </li>
              <li className="flex items-start gap-3">
                <History size={18} className="text-blue-500 mt-0.5" />
                <span>Track all your submissions and earnings</span>
              </li>
            </ul>
          </div>
          
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 transition-colors"
          >
            Upgrade to Contributor
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 page-padding" data-testid="feedback-page">
      <SEO title="Creator Feedback" description="Submit feedback on comics and earn money when creators use your ideas." />
      
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--primary)] mb-4"
        >
          <ArrowLeft size={18} />
          Back to Dashboard
        </Link>
        
        <h1 className="text-4xl font-teko mb-2">Creator Feedback</h1>
        <p className="text-[var(--text-secondary)]">
          Share your ideas with creators and earn money when they're used!
        </p>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto">
        <Tabs defaultValue="submit" className="w-full">
          <TabsList className="w-full justify-start bg-[var(--surface)] border border-[var(--border)] mb-8">
            <TabsTrigger value="submit" className="font-teko text-lg" data-testid="tab-submit">
              <Send size={16} className="mr-2" />
              Submit Feedback
            </TabsTrigger>
            <TabsTrigger value="history" className="font-teko text-lg" data-testid="tab-history">
              <History size={16} className="mr-2" />
              My Submissions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="submit">
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
              <FeedbackSubmitForm onSuccess={() => setRefreshKey(k => k + 1)} />
            </div>
          </TabsContent>

          <TabsContent value="history">
            <FeedbackList key={refreshKey} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default FeedbackPage;
